import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import SpecRender from '../components/SpecRender';
import { registerShortcut } from '../shortcuts';

const API = axios.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
API.interceptors.request.use(cfg => {
  try { const u = JSON.parse(localStorage.getItem('npd_user')); if (u?.token) cfg.headers.Authorization = 'Bearer ' + u.token; } catch {}
  return cfg;
});

const LAYOUTS = [
  { value: 'fields', label: 'Label + Value' },
  { value: 'param_spec', label: 'Parameter / Specification' },
  { value: 'test_req_ref', label: 'Test / Requirement / Reference' },
  { value: 'nutrient_amount_dv', label: 'Nutrient / Amount / %DV' },
];

export default function FormatBuilder() {
  const [templates, setTemplates] = useState([]);
  const [detected, setDetected] = useState(null); // { sections: [...] }
  const [tplName, setTplName] = useState('');
  const [tplDesc, setTplDesc] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(false);
  const fileRef = useRef(null);

  const loadTemplates = () => API.get('/spec-templates').then(r => setTemplates(r.data)).catch(() => {});
  useEffect(() => { loadTemplates(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const isPdf = /\.pdf$/i.test(file.name);
      const r = await API.post(isPdf ? '/spec-templates/detect-pdf' : '/spec-templates/detect', fd);
      setDetected(r.data.detected);
      setEditingId(null);
      setTplName(file.name.replace(/\.(docx|doc)$/i, '') + ' Format');
      toast.success(`Detected ${r.data.detected.sections.length} sections — review and save`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Detection failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const editExisting = (t) => {
    setDetected(t.schema);
    setTplName(t.name);
    setTplDesc(t.description || '');
    setEditingId(t.id);
  };

  const updateSection = (si, patch) => {
    setDetected(d => ({ ...d, sections: d.sections.map((s, i) => i === si ? { ...s, ...patch } : s) }));
  };
  const removeSection = (si) => {
    setDetected(d => ({ ...d, sections: d.sections.filter((_, i) => i !== si) }));
  };
  const updateField = (si, fi, patch) => {
    setDetected(d => ({ ...d, sections: d.sections.map((s, i) => i === si
      ? { ...s, fields: s.fields.map((f, j) => j === fi ? { ...f, ...patch } : f) } : s) }));
  };
  const removeField = (si, fi) => {
    setDetected(d => ({ ...d, sections: d.sections.map((s, i) => i === si
      ? { ...s, fields: s.fields.filter((_, j) => j !== fi) } : s) }));
  };
  const reorderSection = (fromIdx, toIdx, patch) => {
    setDetected(d => {
      const secs = [...d.sections];
      const [moved] = secs.splice(fromIdx, 1);
      const target = fromIdx < toIdx ? toIdx - 1 : toIdx;
      secs.splice(target, 0, { ...moved, ...patch });
      return { ...d, sections: secs };
    });
  };
  const addField = (si) => {
    setDetected(d => ({ ...d, sections: d.sections.map((s, i) => i === si
      ? { ...s, fields: [...s.fields, { key: 'field_' + Date.now(), label: 'New Field' }] } : s) }));
  };

  useEffect(() => {
    if (!detected) return;
    return registerShortcut('save', () => save());
    // eslint-disable-next-line
  }, [detected, tplName, tplDesc, editingId]);

  const save = async () => {
    if (!tplName.trim()) return toast.error('Template name required');
    if (!detected?.sections?.length) return toast.error('No sections to save');
    try {
      if (editingId) {
        await API.put(`/spec-templates/${editingId}`, { name: tplName, description: tplDesc, schema: detected });
        toast.success('Template updated');
      } else {
        await API.post('/spec-templates', { name: tplName, description: tplDesc, schema: detected });
        toast.success('Template saved');
      }
      setDetected(null); setTplName(''); setTplDesc(''); setEditingId(null);
      loadTemplates();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div className="card-title" style={{ margin: 0 }}>Spec Formats ({templates.length})</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Upload a reference Word document to auto-detect its structure, review, and save as a format.</div>
          </div>
          <div>
            <input ref={fileRef} type="file" accept=".docx,.doc,.pdf" style={{ display: 'none' }} onChange={handleUpload} />
            <button className="btn btn-primary" disabled={uploading} onClick={() => fileRef.current?.click()}>
              {uploading ? 'Detecting...' : '⬆ Upload Reference Format'}
            </button>
          </div>
        </div>
        {templates.length > 0 && (
          <table style={{ width: '100%', marginTop: 12 }}>
            <thead><tr><th>Name</th><th>Description</th><th style={{textAlign:'center'}}>Sections</th><th></th></tr></thead>
            <tbody>
              {templates.map(t => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 600 }}>{t.name}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.description}</td>
                  <td style={{ textAlign: 'center' }}>{t.schema?.sections?.length || 0}</td>
                  <td><button className="btn btn-secondary btn-sm" onClick={() => editExisting(t)}>Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detected && (
        <div className="card">
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
            <input className="form-input" style={{ minWidth: 260 }} placeholder="Format name (e.g. FG Spec - Warmleys)" value={tplName} onChange={e => setTplName(e.target.value)} />
            <input className="form-input" style={{ flex: 1, minWidth: 200 }} placeholder="Description (optional)" value={tplDesc} onChange={e => setTplDesc(e.target.value)} />
            <button className="btn btn-secondary" onClick={() => setPreview(p => !p)}>{preview ? '✏ Back to Edit' : '👁 Preview'}</button>
            <button className="btn btn-primary" onClick={save}>💾 {editingId ? 'Update Format' : 'Save Format'}</button>
            <button className="btn btn-secondary" onClick={() => { setDetected(null); setEditingId(null); }}>Cancel</button>
          </div>

          {!preview && detected.sections.map((sec, si) => (
            <div key={si} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12, marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                <input className="form-input" style={{ fontWeight: 700, minWidth: 240 }} value={sec.title}
                  onChange={e => updateSection(si, { title: e.target.value })} />
                <select className="form-input" value={sec.layout} onChange={e => updateSection(si, { layout: e.target.value })}>
                  {LAYOUTS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
                <button className="btn btn-danger btn-sm" onClick={() => removeSection(si)}>Remove Section</button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {sec.fields.map((f, fi) => (
                  <span key={fi} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--bg-hover, #faf5fa)', border: '1px solid var(--border)', borderRadius: 16, padding: '3px 6px 3px 10px' }}>
                    <input value={f.label} onChange={e => updateField(si, fi, { label: e.target.value })}
                      style={{ border: 'none', background: 'transparent', fontSize: 12, width: Math.max(60, f.label.length * 7) + 'px', outline: 'none', color: 'inherit' }} />
                    <button onClick={() => removeField(si, fi)} style={{ border: 'none', background: 'var(--red, #c62828)', color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: 10, cursor: 'pointer', lineHeight: '14px', padding: 0 }}>×</button>
                  </span>
                ))}
                <button className="btn btn-secondary btn-sm" onClick={() => addField(si)}>+ Field</button>
              </div>
            </div>
          ))}

          {preview && (
            <SpecRender schema={detected} docTitle={tplName + ' — format preview'}
              editable
              onSection={updateSection}
              onField={updateField}
              onRemoveField={removeField}
              onRemoveSection={removeSection}
              onAddField={addField}
              onMeta={(patch) => setDetected(d => ({ ...d, ...patch }))}
              onReorder={reorderSection} />
          )}
        </div>
      )}
    </div>
  );
}
